import React, { useEffect, useState, useRef, Fragment, Component } from 'react';
import { Shield, Zap, Check, ArrowRight, AlertTriangle, Download, ChevronDown, ChevronUp, Clock, CreditCard, Star, X, MessageSquare, AlertCircle, Brain, DollarSign, Video, HardHat, CircuitBoard, Cpu, Settings, Target, TrendingUp, Lightbulb, Headphones, ThumbsUp, BookOpen, Lock } from 'lucide-react';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { usePurchaseNotifications } from '../hooks/usePurchaseNotifications';
import { PurchaseNotification } from '../components/PurchaseNotification';
// FAQ Component
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden bg-white" data-id="element-40">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors" data-id="element-41">
        <span className="font-medium text-gray-900 pr-4" data-id="element-42">{question}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-43" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-44" />}
      </button>
      {isOpen && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-45">
          <p className="text-gray-700 leading-relaxed" data-id="element-46">{answer}</p>
        </div>}
    </div>;
}
// Image Carousel Component
function ImageCarousel({
  images
}: {
  images: Array<{
    image: string;
    caption: string;
  }>;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length, isPaused]);
  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
    if ('touches' in e) {
      setTouchStart(e.targetTouches[0].clientX);
    } else {
      setTouchStart((e as React.MouseEvent).clientX);
    }
  };
  const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if ('touches' in e) {
      setTouchEnd(e.targetTouches[0].clientX);
    } else {
      setTouchEnd((e as React.MouseEvent).clientX);
    }
  };
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    if (distance < -50) setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
    setTouchEnd(0);
    setTouchStart(0);
  };
  return <div className="relative max-w-2xl mx-auto touch-pan-y" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onMouseDown={handleTouchStart} onMouseMove={handleTouchMove} onMouseUp={handleTouchEnd} onMouseLeave={handleTouchEnd} data-id="element-47">
      <div className="relative bg-gray-50 border-2 border-gray-200 rounded-xl overflow-hidden shadow-lg select-none cursor-grab active:cursor-grabbing" data-id="element-48">
        <div className="aspect-[3/4] relative" data-id="element-49">
          <img src={images[currentIndex].image} alt={images[currentIndex].caption} className="w-full h-full object-cover transition-opacity duration-500 pointer-events-none" data-id="element-50" />
        </div>
        <div className="p-4 bg-white border-t border-gray-100" data-id="element-51">
          <p className="text-sm font-medium text-gray-700 text-center" data-id="element-52">
            {images[currentIndex].caption}
          </p>
        </div>
      </div>
      <div className="flex justify-center gap-2 mt-6" data-id="element-53">
        {images.map((_, index) => <button key={index} onClick={() => {
        setCurrentIndex(index);
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 5000);
      }} className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-blue-600 w-8' : 'bg-gray-300 w-2'}`} aria-label={`Ir a imagen ${index + 1}`} data-id="element-54" />)}
      </div>
    </div>;
}
// Dynamic Stats Hook
function useDynamicStats() {
  const [viewers, setViewers] = useState(47);
  const [spots, setSpots] = useState(9);
  useEffect(() => {
    const viewerInterval = setInterval(() => {
      setViewers(prev => {
        const change = Math.floor(Math.random() * 5) - 2;
        return Math.max(40, Math.min(55, prev + change));
      });
    }, Math.random() * 5000 + 8000);
    const spotInterval = setInterval(() => {
      setSpots(prev => prev > 3 && Math.random() > 0.65 ? prev - 1 : prev);
    }, 40000);
    return () => {
      clearInterval(viewerInterval);
      clearInterval(spotInterval);
    };
  }, []);
  return {
    viewers,
    spots
  };
}
// Upsell Stats Hook
function useUpsellStats() {
  const [viewers, setViewers] = useState(62);
  const [spots, setSpots] = useState(12);
  const [professionals, setProfessionals] = useState(5851);
  useEffect(() => {
    const viewerInterval = setInterval(() => {
      setViewers(prev => {
        const change = Math.floor(Math.random() * 5) - 2;
        return Math.max(55, Math.min(75, prev + change));
      });
    }, 5000);
    const spotInterval = setInterval(() => {
      setSpots(prev => {
        if (prev > 2) {
          setProfessionals(p => p + 1);
          return prev - 1;
        }
        return prev;
      });
    }, 40000);
    return () => {
      clearInterval(viewerInterval);
      clearInterval(spotInterval);
    };
  }, []);
  return {
    viewers,
    spots,
    professionals
  };
}
// Upsell Section Component
function UpsellSection() {
  const {
    viewers,
    spots,
    professionals
  } = useUpsellStats();
  const today = new Date();
  const formattedDate = today.toLocaleDateString('es-AR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
  const displayDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
  const handleUpsellClick = () => {
    window.location.href = 'https://checkout.maestroelectrico.shop/cart/47989263106270:1';
  };
  return <section className="py-16 px-4 bg-[#000000] font-sans border-t-4 border-[#FFD700]" data-id="element-55">
      <div className="container mx-auto max-w-4xl" data-id="element-56">
        <div className="border-2 border-gray-800 bg-[#050505] p-6 md:p-10 relative overflow-hidden" data-id="element-57">
          {/* Header */}
          <div className="text-center mb-8" data-id="element-58">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white uppercase tracking-wider leading-tight" data-id="element-59">
              ESPECIALIZACIÓN INDUSTRIAL COMPLETA:
              <br data-id="element-60" />
              <span className="text-[#FFD700]" data-id="element-61">
                MAESTRO ELÉCTRICO + PLC MASTER
              </span>
            </h2>
            <div className="bg-[#FFD700] text-black font-black py-3 px-4 mt-6 text-lg md:text-xl uppercase tracking-widest shadow-[0_0_15px_rgba(255,215,0,0.3)]" data-id="element-62">
              ¡COMBO TOTAL PROFESIONAL! INCLUYE: 2 MANUALES + 8 BONOS DE REGALO
            </div>
          </div>

          {/* Body */}
          <div className="mb-10 flex justify-center" data-id="element-63">
            <ul className="space-y-4 text-gray-300 text-lg font-medium inline-block text-left" data-id="element-64">
              <li className="flex items-start gap-3" data-id="element-65">
                <Check className="w-6 h-6 text-[#FFD700] flex-shrink-0 mt-0.5" data-id="element-66" />
                <span data-id="element-67">
                  Programa Completo{' '}
                  <strong className="text-white" data-id="element-68">MAESTRO ELÉCTRICO</strong>
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-69">
                <Check className="w-6 h-6 text-[#FFD700] flex-shrink-0 mt-0.5" data-id="element-70" />
                <span data-id="element-71">
                  Sistema Avanzado{' '}
                  <strong className="text-white" data-id="element-72">PLC MASTER</strong>
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-73">
                <Check className="w-6 h-6 text-[#FFD700] flex-shrink-0 mt-0.5" data-id="element-74" />
                <span data-id="element-75">
                  <strong className="text-[#FFD700]" data-id="element-76">8 BONOS DE REGALO</strong>{' '}
                  exclusivos
                </span>
              </li>
            </ul>
          </div>

          {/* Pricing */}
          <div className="text-center mb-10 border-t border-b border-gray-800 py-8" data-id="element-77">
            <p className="text-gray-500 line-through text-2xl font-bold mb-2" data-id="element-78">
              $99.980 ARS
            </p>
            <p className="text-6xl md:text-7xl font-black text-[#FFD700] leading-none tracking-tighter drop-shadow-[0_0_10px_rgba(255,215,0,0.2)]" data-id="element-79">
              $29.990{' '}
              <span className="text-2xl text-gray-400 font-bold" data-id="element-80">ARS</span>
            </p>
            <div className="mt-4 flex flex-col items-center gap-3" data-id="element-81">
              <span className="bg-red-600 text-white font-black px-4 py-1.5 text-sm uppercase tracking-widest" data-id="element-82">
                AHORRÁ MÁS DEL 60%
              </span>
              <div className="btn-shine bg-gray-900 border border-[#FFD700]/30 text-[#FFD700] font-bold px-6 py-2 rounded-full flex items-center gap-2" data-id="element-83">
                <CreditCard className="w-5 h-5" data-id="element-84" />
                Hasta 3 cuotas sin interés
              </div>
            </div>
          </div>

          {/* Urgency */}
          <div className="mb-10" data-id="element-85">
            <p className="text-center text-red-500 font-bold uppercase tracking-widest mb-6 animate-pulse" data-id="element-86">
              OFERTA VÁLIDA SOLO POR HOY: {displayDate}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-87">
              <div className="bg-black border border-gray-800 p-4 text-center flex flex-col items-center justify-center" data-id="element-88">
                <div className="flex items-center gap-2 mb-1" data-id="element-89">
                  <span className="relative flex h-3 w-3" data-id="element-90">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FFD700] opacity-75" data-id="element-91"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-[#FFD700]" data-id="element-92"></span>
                  </span>
                  <span className="text-2xl font-black text-white" data-id="element-93">
                    {viewers}
                  </span>
                </div>
                <span className="text-xs text-gray-500 uppercase font-bold tracking-wider" data-id="element-94">
                  Personas mirando
                </span>
              </div>
              <div className="bg-black border border-red-900/50 p-4 text-center flex flex-col items-center justify-center" data-id="element-95">
                <span className="text-2xl font-black text-red-500" data-id="element-96">
                  {spots}
                </span>
                <span className="text-xs text-gray-500 uppercase font-bold tracking-wider" data-id="element-97">
                  Cupos disponibles
                </span>
              </div>
              <div className="bg-black border border-gray-800 p-4 text-center flex flex-col items-center justify-center" data-id="element-98">
                <span className="text-2xl font-black text-white" data-id="element-99">
                  {professionals}
                </span>
                <span className="text-xs text-gray-500 uppercase font-bold tracking-wider" data-id="element-100">
                  Ya accedieron
                </span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <button onClick={handleUpsellClick} className="btn-shine w-full bg-[#FFD700] hover:bg-yellow-400 text-black text-xl md:text-3xl font-black py-6 uppercase tracking-tight transition-transform hover:scale-[1.02] shadow-[0_0_30px_rgba(255,215,0,0.3)] flex items-center justify-center gap-3" data-id="element-101">
            SÍ, QUIERO EL COMBO COMPLETO
            <ArrowRight className="w-8 h-8" data-id="element-102" />
          </button>

          {/* Trust */}
          <div className="mt-8 flex flex-wrap justify-center gap-6 md:gap-10 border-t border-gray-800 pt-6" data-id="element-103">
            <div className="flex items-center gap-2" data-id="element-104">
              <Shield className="w-5 h-5 text-gray-500" data-id="element-105" />
              <span className="text-xs text-gray-500 font-bold uppercase tracking-wider" data-id="element-106">
                Pago 100% encriptado
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-107">
              <Check className="w-5 h-5 text-gray-500" data-id="element-108" />
              <span className="text-xs text-gray-500 font-bold uppercase tracking-wider" data-id="element-109">
                Garantía sin riesgo
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-110">
              <Lock className="w-5 h-5 text-gray-500" data-id="element-111" />
              <span className="text-xs text-gray-500 font-bold uppercase tracking-wider" data-id="element-112">
                COMPRA 100% SEGURA
              </span>
            </div>
          </div>
        </div>

        {/* MercadoLider Image Placeholder */}
        <div className="mt-12 flex justify-center" data-id="element-113">
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-full shadow-lg" data-id="element-114">
            <Star className="w-6 h-6 fill-blue-500 text-blue-500" data-id="element-115" />
            <span className="text-blue-900 font-black italic text-xl tracking-tighter" data-id="element-116">
              MercadoLíder
            </span>
            <span className="text-gray-400 font-bold text-xl" data-id="element-117">Platinum</span>
          </div>
        </div>
      </div>
    </section>;
}
// Review Form Component
function ReviewForm({
  onSubmitReview
}: {
  onSubmitReview: (review: {
    name: string;
    text: string;
    stars: number;
  }) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Por favor ingresá tu nombre');
      return;
    }
    if (rating === 0) {
      setError('Por favor seleccioná una calificación');
      return;
    }
    if (!comment.trim()) {
      setError('Por favor escribí un comentario');
      return;
    }
    setError('');
    setShowSuccess(true);
    onSubmitReview({
      name: name.trim(),
      text: comment.trim(),
      stars: rating
    });
    setName('');
    setComment('');
    setRating(0);
    setTimeout(() => {
      setShowSuccess(false);
      setIsOpen(false);
    }, 3000);
  };
  if (!isOpen) {
    return <div className="text-center mt-8" data-id="element-118">
        <button onClick={() => setIsOpen(true)} className="text-gray-500 hover:text-blue-600 text-sm font-medium underline transition-colors" data-id="element-119">
          ¿Compraste? Dejá tu reseña
        </button>
      </div>;
  }
  return <div className="bg-white border border-gray-200 rounded-xl p-6 mt-8 max-w-2xl mx-auto shadow-sm relative animate-fade-in-up" data-id="element-120">
      {showSuccess && <div className="absolute top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium z-10" data-id="element-121">
          <p data-id="element-122">¡Gracias por tu reseña!</p>
        </div>}
      <div className="flex justify-between items-center mb-4" data-id="element-123">
        <h4 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-124">
          <MessageSquare className="w-5 h-5 text-blue-600" data-id="element-125" />
          Dejá tu reseña
        </h4>
        <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600" data-id="element-126">
          <X className="w-5 h-5" data-id="element-127" />
        </button>
      </div>
      <div className="space-y-4" data-id="element-128">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-129">
          <input type="text" placeholder="Tu nombre" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" data-id="element-130" />
          <div className="flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg bg-white" data-id="element-131">
            {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-5 h-5 cursor-pointer transition-colors ${s <= (hoverRating || rating) ? 'fill-orange-400 text-orange-400' : 'text-gray-300'}`} onMouseEnter={() => setHoverRating(s)} onMouseLeave={() => setHoverRating(0)} onClick={() => setRating(s)} data-id="element-132" />)}
          </div>
        </div>
        <textarea placeholder="Contanos tu experiencia..." rows={3} value={comment} onChange={e => setComment(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none" data-id="element-133" />
        {error && <p className="text-red-500 text-sm" data-id="element-134">{error}</p>}
        <button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors text-sm" data-id="element-135">
          Enviar reseña
        </button>
      </div>
    </div>;
}
// Testimonial Item with Like functionality
function TestimonialItem({
  t
}: {
  t: any;
}) {
  const [likes, setLikes] = useState(Math.floor(Math.random() * 100) + 20);
  const [liked, setLiked] = useState(false);
  const handleLike = () => {
    if (liked) {
      setLikes(prev => prev - 1);
      setLiked(false);
    } else {
      setLikes(prev => prev + 1);
      setLiked(true);
    }
  };
  return <div className="flex items-start gap-3" data-id="element-136">
      <div className="w-9 h-9 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" data-id="element-137">
        {t.name.charAt(0)}
      </div>
      <div className="flex-grow" data-id="element-138">
        <div className="flex items-center gap-2 mb-1" data-id="element-139">
          <span className="font-bold text-sm text-gray-800" data-id="element-140">{t.name}</span>
          {t.verified && <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium" data-id="element-141">
              ✓ Verificado
            </span>}
        </div>
        <div className="chat-bubble" data-id="element-142">
          <p className="text-sm text-gray-800 leading-relaxed" data-id="element-143">{t.msg}</p>
          <div className="flex justify-between items-center mt-2" data-id="element-144">
            <p className="text-[10px] text-gray-400" data-id="element-145">{t.time}</p>
            <button onClick={handleLike} className={`flex items-center gap-1 text-[10px] font-medium transition-colors ${liked ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'}`} data-id="element-146">
              <ThumbsUp className={`w-3 h-3 ${liked ? 'fill-blue-600' : ''}`} data-id="element-147" />
              {likes}
            </button>
          </div>
        </div>
      </div>
    </div>;
}
export function LandingPage() {
  const {
    trackEvent
  } = useMetaPixel();
  const {
    notification,
    isVisible
  } = usePurchaseNotifications();
  const {
    viewers,
    spots
  } = useDynamicStats();
  const [headerVisible, setHeaderVisible] = useState(false);
  const [problemRealVisible, setProblemRealVisible] = useState(false);
  const [decisionVisible, setDecisionVisible] = useState(false);
  const [benefitsVisible, setBenefitsVisible] = useState(false);
  const [postHeroVisible, setPostHeroVisible] = useState(false);
  const [visibleTestimonials, setVisibleTestimonials] = useState(5);
  const [activeBonus, setActiveBonus] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState<'combo' | 'single'>('combo');
  const bonusScrollRef = useRef<HTMLDivElement>(null);
  // Reviews pool
  const allChatReviews = [{
    name: 'Javier M.',
    msg: 'Che, posta que me cambió la cabeza. Antes veía el PLC y me daba respeto tocar, ahora entro al código y sé exactamente qué buscar 💪',
    time: '14:32',
    verified: true
  }, {
    name: 'Gustavo R.',
    msg: 'Re sirve loco. Directo al grano para los que estamos en la planta. Los diagramas de lógica están claritos.',
    time: '09:15',
    verified: true
  }, {
    name: 'Pablo S.',
    msg: 'Me salvó las papas con una línea parada. No le encontraban la vuelta y con el método de diagnóstico lo saqué de taquito 🔧',
    time: '18:47',
    verified: true
  }, {
    name: 'Andrés C.',
    msg: 'Vale cada peso. Solo con la guía de fallas comunes ya se paga solo el material. Lo uso siempre.',
    time: '11:03',
    verified: true
  }, {
    name: 'Matías B.',
    msg: 'Laburo en mantenimiento y esto me ordenó la lógica mal. Antes adivinaba, ahora entiendo qué hace la máquina ⚡',
    time: '16:22',
    verified: true
  }];
  // Scroll reveal for sections
  useEffect(() => {
    const revealObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-reveal');
          revealObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });
    document.querySelectorAll('[data-reveal]').forEach(el => {
      revealObserver.observe(el);
    });
    return () => revealObserver.disconnect();
  }, []);
  // Header visibility on scroll
  useEffect(() => {
    const handleScroll = () => {
      setHeaderVisible(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target.id === 'problem-real-section') setProblemRealVisible(true);
          if (entry.target.id === 'decision-section') setDecisionVisible(true);
          if (entry.target.id === 'benefits-section') setBenefitsVisible(true);
          if (entry.target.id === 'post-hero-section') setPostHeroVisible(true);
        }
      });
    }, {
      threshold: 0.15
    });
    const sections = ['problem-real-section', 'decision-section', 'benefits-section', 'post-hero-section'];
    sections.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  const scrollToPurchase = () => {
    document.getElementById('compra')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const scrollToSolution = () => {
    document.getElementById('solution')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handlePurchaseClick = () => {
    trackEvent('InitiateCheckout');
    const url = selectedPackage === 'combo' ? 'https://checkout.maestroelectrico.shop/cart/47989263106270:1' : 'https://checkout.maestroelectrico.shop/cart/47934593335518:1';
    window.location.href = url;
  };
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          trackEvent('ViewContent', {
            content_name: 'Bonuses Section'
          });
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.5
    });
    const bonusSection = document.getElementById('bonuses');
    if (bonusSection) observer.observe(bonusSection);
    return () => observer.disconnect();
  }, [trackEvent]);
  const contentImages = [{
    image: 'https://cdn.magicpatterns.com/uploads/6N9aigZzaNsoRe37pWdoFH/WhatsApp_Image_2026-01-17_at_13.42.51.jpg',
    caption: 'Diagramas explicados paso a paso'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/aRdF2bWiMHEmctuHXsJVR9/WhatsApp_Image_2026-01-17_at_13.42.50_(3).jpg',
    caption: 'Entendé la lógica del circuito, no solo la conexión'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/fuLknpKftbKZSC5DGAQ2tZ/WhatsApp_Image_2026-01-17_at_13.42.50_(2).jpg',
    caption: 'Casos prácticos aplicados al trabajo real'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/aGWc1fPM9DTj6kfR5oKjFr/WhatsApp_Image_2026-01-17_at_13.42.50_(1).jpg',
    caption: 'Material pensado para uso profesional'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/dZNF3VrgumsrSXiMRXLWTh/WhatsApp_Image_2026-01-17_at_13.42.49.jpg',
    caption: 'Explicaciones técnicas claras y directas'
  }];
  const showMoreTestimonials = () => {
    setVisibleTestimonials(prev => Math.min(prev + 5, allChatReviews.length));
  };
  const handleSubmitReview = (review: {
    name: string;
    text: string;
    stars: number;
  }) => {
    // Handle review submission
  };
  const handleBonusScroll = (index: number) => {
    setActiveBonus(index);
    if (bonusScrollRef.current) {
      const cardWidth = 320; // Approx width of card + gap
      bonusScrollRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
    }
  };
  const problemItems = [{
    icon: AlertTriangle,
    title: 'EL MIEDO A LO "DIFÍCIL"',
    desc: 'Sentir que la automatización es solo para ingenieros o genios de la matemática, cuando en realidad es pura lógica de campo.'
  }, {
    icon: Lock,
    title: 'EL MURO LABORAL',
    desc: 'Ver que todos los puestos bien pagados en la industria exigen PLC y sentir que te quedás afuera por no tener la base técnica.'
  }, {
    icon: Zap,
    title: 'EL TECHO DEL ELECTRICISTA',
    desc: 'Estar cansado del esfuerzo físico extremo y los trabajos mal pagados de siempre, buscando pasar a la parte inteligente del rubro.'
  }, {
    icon: Brain,
    title: 'EL LABERINTO DE YOUTUBE',
    desc: 'Perder meses con tutoriales sueltos que no tienen un orden lógico y te terminan dejando más confundido que al principio.'
  }];
  const ecosystemItems = [{
    icon: Video,
    title: '+100 CLASES GRABADAS EN ALTA DEFINICIÓN',
    desc: 'Paso a paso desde la instalación de máquinas virtuales hasta la programación avanzada.'
  }, {
    icon: Cpu,
    title: 'SOFTWARE Y SIMULADORES PROFESIONALES',
    desc: 'Acceso y guía de instalación para Siemens TIA Portal, Allen-Bradley Studio 5000 y RSLogix 500.'
  }, {
    icon: Download,
    title: 'BIBLIOTECA DE MANUALES TÉCNICOS EN PDF',
    desc: 'Guías de ingeniería, diagramas de conexión y hojas de datos listas para consulta en planta.'
  }, {
    icon: HardHat,
    title: 'ENTORNO VIRTUAL BOX PRE-CONFIGURADO',
    desc: 'Olvidate de problemas de compatibilidad; recibís las máquinas virtuales listas para correr los programas.'
  }, {
    icon: CircuitBoard,
    title: 'CURSOS COMPLEMENTARIOS',
    desc: 'Módulos específicos de HMI (Pantallas), SCADA y Redes Industriales (Profinet/Modbus).'
  }];
  const bonuses = [{
    title: 'CURSO COMPLETO DE SCADA',
    desc: 'Visualización y control de procesos en tiempo real.',
    img: 'https://cdn.magicpatterns.com/uploads/vy87Pj4Sj47qBszJjQkhfj/Gemini_Generated_Image_vp50nqvp50nqvp50.png',
    value: '$14.990 ARS',
    label: 'HOY: GRATIS'
  }, {
    title: 'MÓDULO EXTRA DE PLC',
    desc: 'Especialización en Logo Siemens y RSLogix.',
    img: 'https://cdn.magicpatterns.com/uploads/eSbaAdqFeccZmE2NYRzeGL/Gemini_Generated_Image_ofo2vrofo2vrofo2.png',
    value: '$9.990 ARS',
    label: 'HOY: GRATIS'
  }, {
    title: 'CONTROL DE PROCESOS',
    desc: 'Dominio de sistemas continuos y discretos.',
    img: 'https://cdn.magicpatterns.com/uploads/aP18Z5MXiG8xCyEV7bBRs5/Gemini_Generated_Image_o5jpmxo5jpmxo5jp.png',
    value: '$6.990 ARS',
    label: 'HOY: GRATIS'
  }, {
    title: 'VARIADORES Y SENSORES',
    desc: 'Integración avanzada de hardware industrial.',
    img: 'https://cdn.magicpatterns.com/uploads/kanbA5DbyaFJMFSd93R4q2/Gemini_Generated_Image_2bzo742bzo742bzo.png',
    value: '$8.990 ARS',
    label: 'HOY: GRATIS'
  }];
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-800" data-id="element-148">
      {/* Header - Hidden initially, appears on scroll */}
      <header className={`bg-blue-900 text-white fixed top-0 left-0 right-0 z-40 shadow-md transition-all duration-500 ${headerVisible ? 'animate-header-slide' : 'opacity-0 -translate-y-full pointer-events-none'} ${isVisible ? 'py-5' : 'py-3'}`} data-id="element-149">
        <div className="container mx-auto px-4 flex justify-between items-center transition-all duration-500" data-id="element-150">
          <div className="flex items-center gap-3 overflow-hidden flex-1" data-id="element-151">
            <div className={`rounded-full overflow-hidden border-2 border-white/20 animate-logo-pulse flex-shrink-0 transition-all duration-500 ${isVisible ? 'w-12 h-12' : 'w-10 h-10'}`} data-id="element-152">
              <img src="https://cdn.magicpatterns.com/uploads/v3vJ7LASUzqrw9kw6Gkeea/Diseno_sin_titulo_(5).png" alt="PLC MASTER Logo" className="w-full h-full object-cover" data-id="element-153" />
            </div>
            {notification && <PurchaseNotification name={notification.name} city={notification.city} product={notification.product} isVisible={isVisible} isHeader={true} data-id="element-154" />}
          </div>
          <button onClick={scrollToPurchase} className={`${`bg-orange-500 hover:bg-orange-400 text-white font-bold rounded transition-all duration-500 flex-shrink-0 ml-2 ${isVisible ? 'py-3 px-6 text-base' : 'py-2 px-4 text-sm'}`} text-[12px] pt-[5px] pb-[5px] pl-[15px] pr-[15px]`} data-id="element-155">
            VER OFERTA
          </button>
        </div>
      </header>

      {/* HERO - Atmospheric Industrial Background */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-20 pb-8" style={{
      background: 'radial-gradient(ellipse at 50% 50%, #1e40af 0%, #1e3a8a 25%, #172554 50%, #0f1729 80%, #0a1120 100%)'
    }} data-id="element-156">
        <div className="container mx-auto px-4 relative z-10" data-id="element-157">
          {/* Small text above title */}
          <p className="text-center text-[11px] md:text-xs font-medium text-blue-300/70 uppercase tracking-[0.25em] mb-6 animate-hero-text" style={{
          animationDelay: '0s'
        }} data-id="element-158">
            PROGRAMA DE ESPECIALIZACIÓN PARA TÉCNICOS Y ELECTRICISTAS
          </p>

          {/* Main Title - Staggered fade */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-[1.1] text-center" data-id="element-159">
            <span className="block animate-hero-text text-white" style={{
            animationDelay: '0.1s'
          }} data-id="element-160">
              DOMINÁ LA
            </span>
            <span className="block animate-hero-text text-orange-400" style={{
            animationDelay: '0.3s'
          }} data-id="element-161">
              LÓGICA DE PLC
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-center mb-12 font-medium animate-hero-text text-blue-100 max-w-4xl mx-auto" style={{
          animationDelay: '0.5s'
        }} data-id="element-162">
            CONQUISTÁ LA MAESTRÍA EN PROGRAMACIÓN INDUSTRIAL Y ACCEDÉ A LOS{' '}
            <span className="text-orange-300" data-id="element-163">PUESTOS DE MAYOR VALOR</span> EN
            LA INDUSTRIA
          </p>

          {/* Mockup - Premium with glow */}
          <div className="mb-10 flex justify-center relative animate-hero-zoom" style={{
          animationDelay: '0.7s'
        }} data-id="element-164">
            {/* Glow behind mockup */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] bg-blue-500/30 rounded-full blur-[80px]" data-id="element-165"></div>
            <img src="https://cdn.magicpatterns.com/uploads/bTVQjoMUFpSmcJ2JKyhhkY/Diseno_sin_titulo_(6).png" alt="PLC MASTER Libro Digital" className="w-full max-w-xs md:max-w-xl drop-shadow-2xl cursor-pointer relative z-10 hover:scale-[1.02] transition-transform duration-700 rounded-lg" onClick={scrollToPurchase} data-id="element-166" />
          </div>

          {/* Stars + Social proof - Smaller */}
          <div className="text-center mb-8 animate-hero-text" style={{
          animationDelay: '0.9s'
        }} data-id="element-167">
            <div className="flex items-center justify-center gap-0.5 mb-2" data-id="element-168">
              {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-orange-400 text-orange-400" data-id="element-169" />)}
            </div>
            <p className="text-xs font-medium text-white uppercase tracking-wide opacity-90" data-id="element-170">
              DE LOS CREADORES DE MAESTRO ELÉCTRICO (+3.200 ALUMNOS)
            </p>
          </div>

          {/* CTA Button */}
          <div className="text-center animate-hero-text" style={{
          animationDelay: '1.1s'
        }} data-id="element-171">
            <button onClick={scrollToPurchase} className="btn-shine bg-orange-500 hover:bg-orange-400 text-white text-xl md:text-2xl font-bold py-4 px-10 rounded-lg shadow-lg transition-all inline-flex items-center gap-2 animate-cta-pulse hover:scale-105 uppercase" data-id="element-172">
              QUIERO SER UN MAESTRO EN PLC
              <ArrowRight className="w-6 h-6" data-id="element-173" />
            </button>
          </div>
        </div>
      </section>

      {/* POST-HERO - ECOSISTEMA TÉCNICO */}
      <section id="post-hero-section" className="py-12 px-4" style={{
      background: '#0a1929'
    }} data-id="element-174">
        <div className="container mx-auto max-w-6xl" data-id="element-175">
          <div className="text-center mb-10" data-reveal data-id="element-176">
            <h2 className="text-2xl md:text-4xl font-extrabold text-white uppercase tracking-wide mb-2" data-id="element-177">
              UN ECOSISTEMA TÉCNICO COMPLETO EN TUS MANOS
            </h2>
            <div className="h-1 w-24 bg-orange-500 mx-auto rounded-full" data-id="element-178"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-179">
            {ecosystemItems.map((item, i) => <div key={i} className={`bg-blue-900/20 border border-orange-500/20 hover:border-orange-500/50 rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-orange-500/10 ${postHeroVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            animationDelay: `${i * 100}ms`,
            animationFillMode: 'forwards'
          }} data-id="element-180">
                <div className="w-12 h-12 bg-blue-900/50 rounded-lg flex items-center justify-center mb-4 border border-blue-800" data-id="element-181">
                  <item.icon className="w-6 h-6 text-orange-400" data-id="element-182" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 uppercase" data-id="element-183">
                  {item.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed" data-id="element-184">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* EL PROBLEMA REAL - 2x2 Grid */}
      <section id="problem-real-section" className="py-16 px-4 bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 animate-gradient-slow relative overflow-hidden" data-id="element-185">
        {/* Subtle pattern */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-186"></div>

        <div className="container mx-auto max-w-5xl relative z-10" data-id="element-187">
          <h3 className={`text-3xl md:text-4xl font-extrabold text-white mb-12 text-center uppercase tracking-wide transition-all duration-700 ${problemRealVisible ? 'animate-stagger-in' : 'opacity-0'}`} data-id="element-188">
            LO QUE TE IMPIDE{' '}
            <span className="text-orange-400" data-id="element-189">DAR EL SALTO</span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-190">
            {problemItems.map((item, i) => <div key={i} className={`bg-gray-800/80 backdrop-blur-sm border border-gray-700 rounded-xl p-8 shadow-lg transition-all duration-500 hover:border-orange-500/30 ${problemRealVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            animationDelay: `${0.2 + i * 0.1}s`,
            animationFillMode: 'forwards'
          }} data-id="element-191">
                <div className="flex items-start gap-4" data-id="element-192">
                  <div className="w-12 h-12 bg-gray-700/50 rounded-full flex items-center justify-center flex-shrink-0 border border-gray-600" data-id="element-193">
                    <item.icon className="w-6 h-6 text-orange-400" data-id="element-194" />
                  </div>
                  <div data-id="element-195">
                    <h4 className="font-bold text-white text-lg mb-2 uppercase" data-id="element-196">
                      {item.title}
                    </h4>
                    <p className="text-gray-400 leading-relaxed" data-id="element-197">{item.desc}</p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* EL GRAN MITO */}
      <section className="py-12 px-4 bg-[#0a1120] relative overflow-hidden" data-id="element-198">
        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-199">
          <div className="bg-blue-900/20 border border-orange-500/20 rounded-2xl p-8 md:p-12 text-center relative" data-id="element-200">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#0a1120] px-4" data-id="element-201">
              <div className="bg-orange-500/10 border border-orange-500/50 rounded-full p-3" data-id="element-202">
                <Lightbulb className="w-8 h-8 text-orange-400" data-id="element-203" />
              </div>
            </div>

            <h2 className="text-2xl md:text-4xl font-extrabold text-orange-400 mb-6 uppercase leading-tight mt-4" data-id="element-204">
              EL PLC NO ES PARA "GENIOS" NI PARA MATEMÁTICOS
            </h2>

            <p className="text-lg md:text-xl text-white leading-relaxed mb-8 font-light" data-id="element-205">
              Muchos te han hecho creer que para programar un PLC necesitas ser
              un genio o hablar inglés perfecto. Eso es mentira. <br data-id="element-206" />
              <br data-id="element-207" />
              La programación de PLC es pura lógica de campo. Es la misma lógica
              que ya usas para saber por qué un motor no arranca, pero escrita
              en una pantalla. Si sabes interpretar un plano eléctrico, ya
              tienes la mayor parte del camino hecho.
            </p>

            <div className="inline-block border-t border-b border-orange-500/30 py-4 px-8" data-id="element-208">
              <p className="text-xl font-medium text-white" data-id="element-209">
                Lo que te falta no es inteligencia; es un{' '}
                <span className="text-orange-400 font-bold" data-id="element-210">
                  método estructurado
                </span>{' '}
                que te enseñe a pensar como la máquina.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section id="solution" className="py-16 px-4 bg-gradient-to-b from-blue-900 to-blue-800 text-white relative overflow-hidden" data-id="element-211">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/circuit-board.png')] opacity-10" data-id="element-212"></div>
        <div className="container mx-auto max-w-5xl relative z-10" data-id="element-213">
          <div className="text-center mb-12" data-id="element-214">
            <span className="bg-orange-500 text-white font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-4 inline-block" data-id="element-215">
              LA SOLUCIÓN DEFINITIVA
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold mb-4 uppercase leading-tight" data-id="element-216">
              DOMINÁ LA AUTOMATIZACIÓN DESDE CERO CON EL SISTEMA{' '}
              <span className="text-orange-400" data-id="element-217">PLC MASTER</span>
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto" data-id="element-218">
              No es solo un curso, es el ecosistema completo diseñado para que
              pases de electricista básico a programador con criterio
              industrial.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-center mb-12" data-id="element-219">
            <div className="space-y-6" data-id="element-220">
              {[{
              title: 'APRENDIZAJE ESTRUCTURADO PASO A PASO',
              desc: 'De la lógica de contactos básica a la programación compleja en Bloques y Ladder sin saltos de conocimiento.'
            }, {
              title: 'LABORATORIO TÉCNICO EN TU PC',
              desc: 'Aprendé usando los mismos softwares que las grandes plantas: TIA Portal, Studio 5000 y RSLogix incluidos.'
            }, {
              title: 'PRÁCTICA REAL SIN RIESGOS',
              desc: 'Utilizá nuestros simuladores y máquinas virtuales pre-configuradas para probar tus programas antes de tocar un tablero real.'
            }, {
              title: 'BIBLIOTECA DE RECURSOS PROFESIONALES',
              desc: 'Accedé a manuales de ingeniería y lógica de fallas para resolver problemas en planta como un experto desde el primer día.'
            }].map((item, index) => <div key={index} className="flex items-start gap-4 bg-blue-800/50 p-5 rounded-xl border border-blue-700 hover:border-orange-500/30 transition-colors" data-id="element-221">
                  <div className="w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1" data-id="element-222">
                    <Check className="text-orange-400 w-5 h-5 font-bold" data-id="element-223" />
                  </div>
                  <div data-id="element-224">
                    <h4 className="font-bold text-white text-lg mb-1" data-id="element-225">
                      {item.title}
                    </h4>
                    <p className="text-blue-100 text-sm leading-relaxed" data-id="element-226">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>
            <div className="flex justify-center" data-id="element-227">
              <div className="relative" data-id="element-228">
                <div className="absolute inset-0 bg-orange-500/20 blur-3xl rounded-full" data-id="element-229"></div>
                <img src="https://cdn.magicpatterns.com/uploads/uH8CjT84yCiNPHjQAWZG39/Diseno_sin_titulo.png" className="w-full max-w-sm drop-shadow-2xl rounded-lg relative z-10 transform hover:scale-105 transition-transform duration-500" alt="PLC Master System" data-id="element-230" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BONUSES - 4 CARDS WITH SLIDER */}
      <section id="bonuses" className="py-16 px-4 bg-gradient-to-b from-white to-blue-50" data-id="element-231">
        <div className="container mx-auto max-w-6xl" data-id="element-232">
          <div className="text-center mb-10" data-reveal data-id="element-233">
            <h2 className="text-2xl md:text-4xl font-bold text-blue-900 mb-4 tracking-tight relative inline-block uppercase" data-id="element-234">
              ADEMÁS, ASEGURÁ ESTOS{' '}
              <span className="text-orange-500 bg-orange-100 px-2 rounded" data-id="element-235">
                4
              </span>{' '}
              BONOS EXCLUSIVOS DE LANZAMIENTO
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-6 mt-2" data-id="element-236">
              Complementos estratégicos para tu crecimiento como especialista.
            </p>
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-6 py-3 rounded-full font-bold text-sm uppercase tracking-wide" data-id="element-237">
              <Clock className="w-4 h-4" data-id="element-238" />
              GRATIS POR TIEMPO LIMITADO
            </div>
          </div>

          {/* Horizontal Carousel */}
          <div className="relative max-w-5xl mx-auto" data-id="element-239">
            <div ref={bonusScrollRef} className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory hide-scrollbar" style={{
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch'
          }} onScroll={e => {
            const scrollLeft = e.currentTarget.scrollLeft;
            const maxScroll = e.currentTarget.scrollWidth - e.currentTarget.clientWidth;
            if (maxScroll > 0) {
              const progress = scrollLeft / maxScroll;
              const newIndex = Math.round(progress * (bonuses.length - 1));
              if (newIndex !== activeBonus) {
                setActiveBonus(newIndex);
              }
            }
          }} data-id="element-240">
              {bonuses.map((bonus, i) => <div key={i} onClick={scrollToPurchase} className="cursor-pointer group flex-shrink-0 w-[300px] md:w-[340px] snap-center" data-id="element-241">
                  <div className="bg-white rounded-xl overflow-hidden border-2 border-gray-100 transition-all transform hover:-translate-y-2 h-full flex flex-col shadow-lg hover:shadow-2xl" data-id="element-242">
                    <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white px-4 py-2 text-center font-bold text-sm uppercase" data-id="element-243">
                      {bonus.label}
                    </div>
                    <div className="p-5 flex flex-col flex-grow" data-id="element-244">
                      <div className="aspect-[3/4] mb-4 bg-gray-100 rounded-lg overflow-hidden relative group-hover:scale-[1.02] transition-transform duration-500" data-id="element-245">
                        <img src={bonus.img} alt={bonus.title} className="w-full h-full object-cover" data-id="element-246" />
                      </div>
                      <h3 className="font-bold text-lg text-gray-900 mb-3 leading-tight uppercase" data-id="element-247">
                        {bonus.title}
                      </h3>
                      <p className="text-sm text-gray-700 mb-4 leading-relaxed flex-grow" data-id="element-248">
                        {bonus.desc}
                      </p>
                      <p className="text-sm text-gray-600 mb-2" data-id="element-249">
                        Valor:{' '}
                        <span className="line-through text-gray-400" data-id="element-250">
                          {bonus.value}
                        </span>
                      </p>
                      <div className="bg-green-50 border-green-200 border rounded-lg px-3 py-2 mt-auto flex items-center gap-2" data-id="element-251">
                        <Check className="w-4 h-4 text-green-600" data-id="element-252" />
                        <p className="text-sm font-bold text-green-700" data-id="element-253">
                          Incluido sin costo
                        </p>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>

            {/* Slider Progress Bar */}
            <div className="mt-6 flex flex-col items-center gap-3" data-id="element-254">
              <div className="w-full max-w-xs h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-255">
                <div className="h-full bg-orange-500 rounded-full transition-all duration-300 ease-out" style={{
                width: `${(activeBonus + 1) / bonuses.length * 100}%`
              }} data-id="element-256"></div>
              </div>
              <p className="text-sm text-gray-400 font-medium" data-id="element-257">
                Desliza para ver todos los bonos
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* LO QUE TE LLEVÁS */}
      <section id="benefits-section" className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white" data-id="element-258">
        <div className="container mx-auto max-w-5xl" data-id="element-259">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-12 text-center uppercase" data-id="element-260">
            LO QUE LOGRÁS CON{' '}
            <span className="text-orange-500" data-id="element-261">PLC MASTER</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-262">
            {[{
            icon: Shield,
            title: 'AUTONOMÍA TÉCNICA',
            items: ['Resolvé fallas sin depender de terceros', 'Tomá decisiones con criterio propio'],
            tag: 'LÓGICA DE CONTROL'
          }, {
            icon: Target,
            title: 'PRECISIÓN EN EL DIAGNÓSTICO',
            items: ['Identificá el error exacto en el código', 'Menos tiempo de máquina parada'],
            tag: 'CERO IMPROVISACIÓN'
          }, {
            icon: TrendingUp,
            title: 'SALTO PROFESIONAL REAL',
            items: ['Pasá de realizar tareas básicas a liderar la automatización en tu zona o planta', 'Respaldo técnico de un especialista'],
            tag: 'VALOR DE MERCADO'
          }].map((card, i) => <div key={i} className={`bg-white border border-gray-200 rounded-xl p-8 shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:border-orange-200 ${benefitsVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            animationDelay: `${i * 100}ms`,
            animationFillMode: 'forwards'
          }} data-id="element-263">
                <div className="flex items-center gap-3 mb-6" data-id="element-264">
                  <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center" data-id="element-265">
                    <card.icon className="w-6 h-6 text-orange-500" data-id="element-266" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg leading-tight" data-id="element-267">
                    {card.title}
                  </h3>
                </div>
                <div className="space-y-4 mb-6" data-id="element-268">
                  {card.items.map((item, j) => <div key={j} className="flex items-start gap-3" data-id="element-269">
                      <Check className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" data-id="element-270" />
                      <span className="text-gray-700 text-sm leading-relaxed" data-id="element-271">
                        {item}
                      </span>
                    </div>)}
                </div>
                <span className="inline-block bg-orange-100 text-orange-700 text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide" data-id="element-272">
                  {card.tag}
                </span>
              </div>)}
          </div>

          {/* Orange Quote Box */}
          <div className="mt-16 bg-orange-500 text-white p-8 md:p-10 rounded-2xl text-center shadow-xl shadow-orange-500/20 max-w-4xl mx-auto transform hover:scale-[1.01] transition-transform" data-id="element-273">
            <p className="text-xl md:text-2xl font-bold leading-relaxed" data-id="element-274">
              "Dejá de ser un técnico limitado por el software y convertite en
              el cerebro de la planta. Invertir en criterio PLC es asegurarte
              los mejores sueldos y el respeto de la industria."
            </p>
          </div>
        </div>
      </section>

      {/* PACK SECTION */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-gray-50" data-id="element-275">
        <div className="container mx-auto max-w-4xl" data-id="element-276">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-12 text-center" data-id="element-277">
            Contenido técnico incluido en el acceso
          </h2>

          <div className="bg-white border-2 border-blue-900 rounded-xl p-8 shadow-xl" data-id="element-278">
            <div className="mb-10 flex justify-center" data-id="element-279">
              <img src="https://cdn.magicpatterns.com/uploads/b5nqhaqnhMVwpzHf7U4vAv/WhatsApp_Image_2026-02-25_at_15.10.25.jpg" className="w-full rounded-lg shadow-lg" alt="Contenido del pack" data-id="element-280" />
            </div>

            <div className="space-y-6" data-id="element-281">
              {[{
              main: true,
              title: 'Curso PLC MASTER: Lógica y Programación Industrial',
              desc: 'Lógica Ladder y bloques explicados con criterio profesional'
            }, {
              main: false,
              title: 'Bono 1: Curso completo de SCADA',
              desc: 'Visualización y control de procesos en tiempo real'
            }, {
              main: false,
              title: 'Bono 2: Módulo extra de PLC',
              desc: 'Especialización en Logo Siemens y RSLogix'
            }, {
              main: false,
              title: 'Bono 3: Control de procesos',
              desc: 'Dominio de sistemas continuos y discretos'
            }, {
              main: false,
              title: 'Bono 4: Variadores y Sensores',
              desc: 'Integración avanzada de hardware industrial'
            }].map((item, i) => <div key={i} className={`flex items-start gap-4 pb-5 ${i < 4 ? 'border-b border-gray-100' : ''}`} data-id="element-282">
                  <div className={`w-6 h-6 ${item.main ? 'bg-orange-500' : 'bg-blue-600'} rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-sm`} data-id="element-283">
                    <Check className={`w-3.5 h-3.5 text-white font-bold`} data-id="element-284" />
                  </div>
                  <div data-id="element-285">
                    <p className={`text-lg font-bold ${item.main ? 'text-blue-900' : 'text-gray-800'} mb-1`} data-id="element-286">
                      {item.title}
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-287">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* BONO EXTRA EXCLUSIVO - ACCESO VITALICIO */}
      <section className="py-12 px-4 bg-white" data-id="element-288">
        <div className="container mx-auto max-w-4xl" data-id="element-289">
          <div className="bg-gradient-to-br from-blue-900 to-blue-950 rounded-3xl overflow-hidden shadow-2xl border-4 border-orange-400/30 relative" data-id="element-290">
            {/* Decorative background elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 blur-3xl rounded-full -mr-32 -mt-32" data-id="element-291"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 blur-3xl rounded-full -ml-32 -mb-32" data-id="element-292"></div>

            <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 p-8 md:p-12" data-id="element-293">
              {/* Image Column */}
              <div className="w-full md:w-2/5 flex justify-center" data-id="element-294">
                <div className="relative group" data-id="element-295">
                  <div className="absolute inset-0 bg-orange-500/20 blur-2xl rounded-full group-hover:bg-orange-500/40 transition-colors duration-500" data-id="element-296"></div>
                  <img src="https://cdn.magicpatterns.com/uploads/w8PJr1f91cKGQkijsD98DU/Gemini_Generated_Image_ajg2w8ajg2w8ajg2.png" alt="Acceso Vitalicio" className="w-full max-w-[280px] relative z-10 transform group-hover:scale-105 transition-transform duration-500 drop-shadow-[0_0_20px_rgba(255,165,0,0.3)]" data-id="element-297" />
                </div>
              </div>

              {/* Content Column */}
              <div className="w-full md:w-3/5 text-center md:text-left" data-id="element-298">
                <div className="inline-block bg-orange-500 text-white font-black px-4 py-1.5 rounded-lg text-xs uppercase tracking-[0.2em] mb-4 shadow-lg shadow-orange-500/20" data-id="element-299">
                  BONO EXTRA EXCLUSIVO
                </div>
                <h3 className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight uppercase tracking-tight" data-id="element-300">
                  ACCESO <span className="text-orange-400" data-id="element-301">VITALICIO</span>
                </h3>
                <p className="text-blue-100 text-lg md:text-xl leading-relaxed mb-8 font-medium opacity-90" data-id="element-302">
                  Olvidate de las mensualidades. Con un solo pago tenés acceso
                  de por vida a todo el material y a las futuras actualizaciones
                  sin pagar un centavo más.
                </p>
                <div className="inline-flex items-center gap-3 bg-green-500/20 border border-green-500/50 px-6 py-3 rounded-2xl" data-id="element-303">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" data-id="element-304"></div>
                  <span className="text-green-400 font-black text-lg uppercase tracking-wider" data-id="element-305">
                    INCLUIDO GRATIS
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PURCHASE SECTION */}
      <section id="compra" className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white" data-id="element-306">
        <div className="container mx-auto max-w-lg" data-id="element-307">
          <div className="bg-white rounded-2xl border-[3px] border-orange-400 relative mb-6 overflow-hidden animate-card-glow" data-reveal data-id="element-308">
            {/* Top orange banner */}
            <div className="bg-orange-500 text-white font-extrabold py-3 px-4 text-center text-sm uppercase tracking-wider" data-id="element-309">
              DESCUENTO ESPECIAL
            </div>

            <div className="p-6 md:p-8" data-id="element-310">
              {/* Package Selector */}
              <div className="flex flex-col gap-3 mb-6" data-id="element-311">
                {/* Combo Option */}
                <div onClick={() => setSelectedPackage('combo')} className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all ${selectedPackage === 'combo' ? 'border-[#FFD700] bg-yellow-50/50 shadow-[0_0_15px_rgba(255,215,0,0.15)]' : 'border-gray-200 hover:border-orange-300'}`} data-id="element-312">
                  {selectedPackage === 'combo' && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FFD700] text-black text-[10px] font-black px-3 py-1 rounded-full flex items-center gap-1 uppercase tracking-wider shadow-sm whitespace-nowrap" data-id="element-313">
                      <Star className="w-3 h-3 fill-black" data-id="element-314" />
                      MÁS ELEGIDA
                    </div>}
                  <div className="flex items-center gap-4" data-id="element-315">
                    <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200 bg-white" data-id="element-316">
                      <img src="https://cdn.magicpatterns.com/uploads/cperWhZMkeRFw4cUPH5onz/aasdadasdasd.webp" alt="Combo" className="w-full h-full object-cover" data-id="element-317" />
                    </div>
                    <div className="flex-grow text-left" data-id="element-318">
                      <h4 className="font-bold text-sm leading-tight mb-1 text-gray-900" data-id="element-319">
                        COMBO PLC MASTER + MAESTRO ELÉCTRICO
                      </h4>
                      <p className="text-xs text-gray-500" data-id="element-320">
                        Programa Completo + 8 Bonos
                      </p>
                    </div>
                    <div className="flex-shrink-0 text-right" data-id="element-321">
                      <p className={`font-black ${selectedPackage === 'combo' ? 'text-yellow-600' : 'text-green-600'}`} data-id="element-322">
                        $29.990
                      </p>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ml-auto mt-1 ${selectedPackage === 'combo' ? 'border-[#FFD700]' : 'border-gray-300'}`} data-id="element-323">
                        {selectedPackage === 'combo' && <div className="w-2.5 h-2.5 bg-[#FFD700] rounded-full" data-id="element-324" />}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Single Option */}
                <div onClick={() => setSelectedPackage('single')} className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${selectedPackage === 'single' ? 'border-orange-500 bg-orange-50/50' : 'border-gray-200 hover:border-orange-300'}`} data-id="element-325">
                  <div className="flex items-center gap-4" data-id="element-326">
                    <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200 bg-white flex items-center justify-center p-1" data-id="element-327">
                      <img src="https://cdn.magicpatterns.com/uploads/bTVQjoMUFpSmcJ2JKyhhkY/Diseno_sin_titulo_(6).png" alt="Single" className="w-full h-full object-contain" data-id="element-328" />
                    </div>
                    <div className="flex-grow text-left" data-id="element-329">
                      <h4 className="font-bold text-gray-900 text-sm leading-tight mb-1" data-id="element-330">
                        SOLO PLC MASTER
                      </h4>
                      <p className="text-xs text-gray-500" data-id="element-331">
                        Sistema Avanzado + 4 Bonos
                      </p>
                    </div>
                    <div className="flex-shrink-0 text-right" data-id="element-332">
                      <p className="font-black text-green-600" data-id="element-333">$19.990</p>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ml-auto mt-1 ${selectedPackage === 'single' ? 'border-orange-500' : 'border-gray-300'}`} data-id="element-334">
                        {selectedPackage === 'single' && <div className="w-2.5 h-2.5 bg-orange-500 rounded-full" data-id="element-335" />}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Strikethrough price */}
              <div className="text-center mb-1" data-id="element-336">
                <p className="text-sm text-red-500 font-medium" data-id="element-337">
                  Antes{' '}
                  <span className="line-through" data-id="element-338">
                    ${selectedPackage === 'combo' ? '99.980' : '49.900'} ARS
                  </span>
                </p>
              </div>

              {/* Main price */}
              <div className="text-center mb-5" data-id="element-339">
                <p className="text-5xl md:text-6xl font-extrabold text-green-600 leading-none" data-id="element-340">
                  ${selectedPackage === 'combo' ? '29.990' : '19.990'}{' '}
                  <span className="text-lg font-normal text-gray-500" data-id="element-341">ARS</span>
                </p>
              </div>

              {/* Installments badge */}
              <div className="flex justify-center mb-6" data-id="element-342">
                <div className="bg-green-50 text-green-700 font-semibold px-5 py-2 rounded-lg text-sm flex items-center gap-2 border border-green-200" data-id="element-343">
                  <CreditCard className="w-4 h-4" data-id="element-344" />
                  Hasta 3 cuotas sin interés
                </div>
              </div>

              {/* Included Items List */}
              <div className="bg-gray-50 rounded-xl p-5 mb-6 border border-gray-100" data-id="element-345">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4" data-id="element-346">
                  ¿QUÉ ESTÁS LLEVANDO?
                </p>
                <ul className="space-y-2.5" data-id="element-347">
                  {(selectedPackage === 'combo' ? ['Manual Maestro Eléctrico', 'Manual PLC Master (Industrial)', 'Bono 1: Curso Paneles Solares', 'Bono 2: Plantillas Presupuesto', 'Bono 3: Eficiencia en Motores', 'Bono 4: Protocolo Verificación', 'Bono 5: Reglamentaciones AEA', 'Bono 6: Lógica de Programación', 'Bono 7: Planos Industriales', 'Bono 8: Diagnóstico de Fallas', 'Bono 9: Certificado Final'] : ['Manual PLC Master (Industrial)', 'Bono 1: Curso completo de SCADA', 'Bono 2: Módulo extra de PLC', 'Bono 3: Control de procesos', 'Bono 4: Variadores y Sensores']).map((item, idx) => <li key={idx} className="flex items-start gap-2.5 text-sm" data-id="element-348">
                      <div className="bg-green-500 rounded-full p-0.5 mt-0.5 flex-shrink-0" data-id="element-349">
                        <Check className="w-3 h-3 text-white font-bold" data-id="element-350" />
                      </div>
                      <span className="text-gray-700 font-medium leading-tight" data-id="element-351">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>

              {/* Urgency box - Moved above button */}
              <div className="bg-[#0b1a2e] rounded-lg px-5 py-4 mb-6" data-id="element-352">
                <div className="flex items-center gap-2 mb-2" data-id="element-353">
                  <span className="relative flex h-2.5 w-2.5 flex-shrink-0" data-id="element-354">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 animate-green-blink" data-id="element-355"></span>
                  </span>
                  <span className="text-sm text-white font-bold" data-id="element-356">
                    40 personas viendo esta página ahora
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-357">
                  <span className="relative flex h-2.5 w-2.5 flex-shrink-0" data-id="element-358">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" data-id="element-359"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" data-id="element-360"></span>
                  </span>
                  <span className="text-sm text-white font-bold" data-id="element-361">
                    Quedan 3 cupos con precio promocional
                  </span>
                </div>
              </div>

              {/* Main CTA Button */}
              <button onClick={handlePurchaseClick} className="btn-shine w-full bg-orange-500 hover:bg-orange-400 text-white text-lg font-extrabold py-4 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 mb-6" data-id="element-362">
                QUIERO ACCESO COMPLETO AHORA ➡️
              </button>

              {/* Payment methods */}
              <div className="text-center mb-6" data-id="element-363">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-364">
                  MEDIOS DE PAGO
                </p>
                <div className="flex flex-col items-center gap-4" data-id="element-365">
                  <img src="https://cdn.magicpatterns.com/uploads/gxJ13jdAqycrJ4WCnDAsQm/mercadopago2.jpg" alt="Mercado Pago y tarjetas" className="w-full max-w-[320px] object-contain" data-id="element-366" />
                  <img src="https://cdn.magicpatterns.com/uploads/qNbxV7QwamptVb4nzTb1SB/capture_260410_163646.png" alt="Pago seguro garantizado" className="w-full max-w-[400px] object-contain" data-id="element-367" />
                </div>
              </div>

              {/* Trust footer text */}
              <div className="flex items-center justify-center gap-6 pt-4 border-t border-gray-200" data-id="element-368">
                <div className="flex items-center gap-1.5" data-id="element-369">
                  <Shield className="w-4 h-4 text-green-500" data-id="element-370" />
                  <span className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide" data-id="element-371">
                    Pago 100% encriptado
                  </span>
                </div>
                <div className="w-px h-5 bg-gray-200" data-id="element-372"></div>
                <div className="flex items-center gap-1.5" data-id="element-373">
                  <img src="https://cdn.magicpatterns.com/uploads/tUHvrtYMuSGXVHTScHjzak/Selo-de-Garantia-de-7-Dias-PNG-Transparente-Sem-Fundo-1.png" alt="Garantía" className="w-5 h-5 object-contain" data-id="element-374" />
                  <span className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide" data-id="element-375">
                    Garantía sin riesgo
                  </span>
                </div>
              </div>
            </div>

            {/* Trust badges on dark background */}
            <div className="bg-gradient-to-br from-gray-900 to-blue-950 p-6 border-t-2 border-gray-700" data-id="element-376">
              <div className="grid grid-cols-2 gap-4 items-center" data-id="element-377">
                <div className="flex flex-col items-center gap-2" data-id="element-378">
                  <img src="https://cdn.magicpatterns.com/uploads/tUHvrtYMuSGXVHTScHjzak/Selo-de-Garantia-de-7-Dias-PNG-Transparente-Sem-Fundo-1.png" alt="Garantía de 7 días" className="w-full max-w-[120px] object-contain" data-id="element-379" />
                  <span className="text-white text-xs font-medium uppercase tracking-wide text-center" data-id="element-380">
                    GARANTÍA DE SATISFACCIÓN
                  </span>
                </div>
                <div className="flex flex-col items-center gap-2" data-id="element-381">
                  <img src="https://cdn.magicpatterns.com/uploads/bmodW1kGNGDhBh2dosY7h5/safe-purchase-7779878_1280.png" alt="Compra Segura" className="w-full max-w-[156px] object-contain" data-id="element-382" />
                  <span className="text-white text-xs font-medium uppercase tracking-wide text-center" data-id="element-383">
                    COMPRA 100% SEGURA
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Post-Purchase Benefits Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center py-4" data-id="element-384">
            <div className="flex flex-col items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-385">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600" data-id="element-386">
                <Check className="w-4 h-4" data-id="element-387" />
              </div>
              <span className="text-sm font-medium text-gray-700" data-id="element-388">
                Acceso de por vida
              </span>
            </div>
            <div className="flex flex-col items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-389">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600" data-id="element-390">
                <Download className="w-4 h-4" data-id="element-391" />
              </div>
              <span className="text-sm font-medium text-gray-700" data-id="element-392">
                Acceso instantáneo
              </span>
            </div>
            <div className="flex flex-col items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-393">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600" data-id="element-394">
                <Headphones className="w-4 h-4" data-id="element-395" />
              </div>
              <span className="text-sm font-medium text-gray-700" data-id="element-396">
                Soporte técnico
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT PREVIEW */}
      <section className="py-16 px-4 bg-white" data-id="element-397">
        <div className="container mx-auto max-w-6xl" data-id="element-398">
          <div className="text-center mb-12" data-id="element-399">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-400">
              Así es el material por dentro
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-401">
              Ejemplos reales de las páginas que vas a usar en el trabajo diario
            </p>
          </div>

          <div className="space-y-16" data-id="element-402">
            <div className="flex justify-center" data-id="element-403">
              <div className="w-full max-w-2xl bg-gray-900 rounded-xl overflow-hidden shadow-2xl border-4 border-gray-100" data-id="element-404">
                <video src="https://cdn.magicpatterns.com/uploads/au6q7KQ39reudghcBDBGdL/WhatsApp Video 2026-02-25 at 13.33.35.mp4" className="w-full" autoPlay loop muted playsInline data-id="element-405" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-100 to-gray-50" data-id="element-406">
        <div className="container mx-auto max-w-2xl" data-reveal data-id="element-407">
          <div className="text-center mb-10" data-id="element-408">
            <div className="flex items-center justify-center gap-1 mb-3" data-id="element-409">
              {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-5 h-5 fill-orange-400 text-orange-400" data-id="element-410" />)}
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 uppercase tracking-tight" data-id="element-411">
              MÁS DE 3.200 ALUMNOS RESPALDAN NUESTRA COMUNIDAD
            </h2>
            <p className="text-sm text-gray-500 mt-2 font-medium" data-id="element-412">
              4,9/5 — 487 reseñas verificadas
            </p>
          </div>

          {/* WhatsApp-style chat bubbles */}
          <div className="space-y-4 mb-8" data-id="element-413">
            {allChatReviews.slice(0, visibleTestimonials).map((t, i) => <TestimonialItem key={i} t={t} data-id="element-414" />)}
          </div>

          {visibleTestimonials < allChatReviews.length && <div className="text-center" data-id="element-415">
              <button onClick={showMoreTestimonials} className="btn-shine bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium py-3 px-8 rounded-full transition-all shadow-sm hover:shadow-md text-sm" data-id="element-416">
                Cargar 5 comentarios más
              </button>
            </div>}

          <ReviewForm onSubmitReview={handleSubmitReview} data-id="element-417" />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-white" data-id="element-418">
        <div className="container mx-auto max-w-3xl" data-id="element-419">
          <div className="text-center mb-12" data-id="element-420">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-421">
              Resolvé acá tus dudas
            </h2>
            <p className="text-lg text-gray-600" data-id="element-422">
              Hacé clic para ver la respuesta que necesitás
            </p>
          </div>

          <div className="space-y-3" data-id="element-423">
            <FAQItem question="¿Qué tipo de contenido incluye PLC MASTER?" answer="Incluye un manual completo de lógica y programación industrial, con explicación clara de Ladder, Bloques, y ejemplos aplicados al trabajo profesional." data-id="element-424" />
            <FAQItem question="¿Necesito ser ingeniero para entender este material?" answer="No. El material está diseñado para técnicos y electricistas, explicando los conceptos de forma clara y progresiva, enfocándose en la lógica de campo." data-id="element-425" />
            <FAQItem question="¿Voy a aprender a interpretar lógica Ladder y Bloques?" answer="Sí. El foco principal es ayudarte a interpretar, entender y estructurar programas en lenguajes Ladder y Bloques de función." data-id="element-426" />
            <FAQItem question="¿Incluye ejemplos prácticos de fallas reales?" answer="Sí. El contenido incluye una biblioteca de lógica para fallas comunes y situaciones reales de planta." data-id="element-427" />
            <FAQItem question="¿En qué formato recibo el material y los simuladores?" answer="Todo el contenido se entrega en formato digital con acceso inmediato a la plataforma donde podrás descargar los manuales y acceder a los simuladores." data-id="element-428" />
            <FAQItem question="¿Tengo acceso para siempre incluyendo actualizaciones?" answer="Sí. El acceso es permanente y recibirás todas las actualizaciones futuras sin costo adicional." data-id="element-429" />
            <FAQItem question="¿Necesito conocimientos avanzados de informática?" answer="No. Si sabes usar una PC básica, puedes seguir el programa. Nos enfocamos en la lógica, no en la informática compleja." data-id="element-430" />
          </div>
        </div>
      </section>

      <UpsellSection data-id="element-431" />

      {/* FOOTER */}
      <footer className="bg-gray-900 text-gray-400 py-10 px-4 text-center text-sm border-t border-gray-800" data-id="element-432">
        <div className="container mx-auto" data-id="element-433">
          <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-434">
            <img src="https://cdn.magicpatterns.com/uploads/v3vJ7LASUzqrw9kw6Gkeea/Diseno_sin_titulo_(5).png" alt="Logo" className="w-8 h-8 object-contain opacity-50 grayscale" data-id="element-435" />
          </div>
          <p className="mb-2" data-id="element-436">
            &copy; {new Date().getFullYear()} PLC MASTER. Todos los derechos
            reservados.
          </p>
          <p className="text-gray-500" data-id="element-437">
            Lógica y programación industrial para profesionales.
          </p>
        </div>
      </footer>
    </div>;
}